// AuthLayout und AuthCard (DESIGN §3.13): Seitengrund ohne Rail, Monogramm oben links, Spalte 400
// ab 18 % der Höhe, Titel `display`, Formkarte mit Innenabstand 24, Persona-Kacheln im Demo-Modus.
// Felder und Knöpfe der Formulare liefert `steuerung`; die Seiten baut Modul `konten`.
import { Copy, WarningCircle } from '@phosphor-icons/react';
import { type ReactNode, useState } from 'react';
import { Avatar } from '../kern/Avatar';
import { cn } from '../kern/cn';
import { Spinner } from '../kern/Misc';
import { Monogram, type Platform, PlatformBadge } from '../kern/Monogram';

export interface AuthLayoutProps {
  platform?: Platform;
  title: string;
  subtitle?: ReactNode;
  /** Oben rechts: CompanyPill, ThemeToggle. */
  topRight?: ReactNode;
  /** Unter der Karte: Persona-Kacheln, Links. */
  footer?: ReactNode;
  appName?: string;
  children: ReactNode;
}

// Dezente Rauten rechts unten (N6: einfarbig grau statt Akzent, keine Deko-Farbe).
function AuthArt() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 480 480"
      className="pointer-events-none fixed right-0 bottom-0 hidden size-120 text-fg-decor opacity-20 lg:block"
    >
      {Array.from({ length: 5 }, (_, row) =>
        Array.from({ length: 5 }, (_, col) => {
          const x = 60 + col * 90;
          const y = 60 + row * 90;
          const s = 18 + ((row * 5 + col) % 4) * 8;
          return (
            <rect
              key={`${row}-${col}`}
              x={x - s / 2}
              y={y - s / 2}
              width={s}
              height={s}
              rx={4}
              transform={`rotate(45 ${x} ${y})`}
              fill="currentColor"
              fillOpacity={0.15 + ((row + col) % 3) * 0.12}
            />
          );
        }),
      )}
    </svg>
  );
}

export function AuthLayout({
  platform = 'people',
  title,
  subtitle,
  topRight,
  footer,
  appName = 'Bar',
  children,
}: AuthLayoutProps) {
  return (
    <div className="relative min-h-dvh overflow-x-hidden bg-page text-fg">
      <AuthArt />
      <header className="relative flex h-topbar items-center justify-between gap-3 px-4 md:px-6">
        <span className="inline-flex items-center gap-2">
          <Monogram size={36} appName={appName} label={appName} />
          {platform !== 'people' ? <PlatformBadge platform={platform} /> : null}
        </span>
        {topRight ? <div className="flex items-center gap-2">{topRight}</div> : null}
      </header>
      <main
        id="main"
        className="relative mx-auto w-full max-w-[calc(var(--spacing-auth)+32px)] px-4 pt-[max(24px,calc(18dvh-68px))] pb-16"
      >
        <h1 className="text-display text-fg">{title}</h1>
        {subtitle ? <p className="mt-2 text-body text-fg-secondary">{subtitle}</p> : null}
        <div className="mt-6">{children}</div>
        {footer ? <div className="mt-6">{footer}</div> : null}
      </main>
    </div>
  );
}

export interface AuthCardProps {
  /** Fehler-Zusammenfassung oben in der Karte (role="alert"). */
  error?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function AuthCard({ error, children, className }: AuthCardProps) {
  return (
    <div className={cn('flex flex-col gap-4 rounded-xl bg-card p-6', className)}>
      {error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5 text-label text-danger-fg"
        >
          <WarningCircle size={18} aria-hidden className="mt-px shrink-0" />
          <span>{error}</span>
        </div>
      ) : null}
      {children}
    </div>
  );
}

/** Trenner „or“ zwischen zwei Linien. */
export function AuthDivider({ label = 'or' }: { label?: string }) {
  return (
    <div
      className="flex items-center gap-3 text-label text-fg-tertiary"
      role="separator"
      aria-label={label}
    >
      <span className="h-px flex-1 bg-line-subtle" />
      {label}
      <span className="h-px flex-1 bg-line-subtle" />
    </div>
  );
}

export interface Persona {
  key: string;
  name: string;
  role: string;
  id?: string;
}

export interface PersonaTilesProps {
  personas: readonly Persona[];
  onSelect: (persona: Persona) => void;
  /** Kachel mit Spinner, während die Anmeldung läuft. */
  pendingKey?: string;
  demoPassword?: string;
}

/** Persona-Kacheln (Demo, K1): 2 Spalten, ab md 3; Klick meldet an. */
export function PersonaTiles({
  personas,
  onSelect,
  pendingKey,
  demoPassword = 'bar-demo-2026',
}: PersonaTilesProps) {
  const [copied, setCopied] = useState(false);
  return (
    <section aria-labelledby="persona-title" className="flex flex-col gap-3">
      <h2 id="persona-title" className="text-label text-fg-secondary">
        Sign in as a demo persona
      </h2>
      <ul className="grid grid-cols-2 gap-2 md:grid-cols-3">
        {personas.map((persona) => {
          const pending = pendingKey === persona.key;
          return (
            <li key={persona.key}>
              <button
                type="button"
                aria-busy={pending || undefined}
                disabled={pendingKey !== undefined}
                onClick={() => onSelect(persona)}
                className="flex h-18 w-full items-center gap-3 rounded-xl bg-tile px-3 text-left transition-colors duration-(--bar-dur-fast) hover:bg-tile-hover disabled:cursor-wait"
              >
                <Avatar
                  name={persona.name}
                  id={persona.id ?? persona.key}
                  size={40}
                  surface="tile"
                  decorative
                />
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-body text-fg">{persona.name}</span>
                  <span className="truncate text-label text-fg-secondary">{persona.role}</span>
                </span>
                {pending ? <Spinner /> : null}
              </button>
            </li>
          );
        })}
      </ul>
      <p className="flex items-center gap-2 text-label text-fg-tertiary">
        Demo password: <code className="font-mono text-fg-secondary">{demoPassword}</code>
        <button
          type="button"
          aria-label={copied ? 'Copied' : 'Copy demo password'}
          className="inline-flex size-8 items-center justify-center rounded-full hover:bg-pill-page"
          onClick={() => {
            void navigator.clipboard?.writeText(demoPassword).then(() => setCopied(true));
          }}
        >
          <Copy size={16} aria-hidden />
        </button>
        <span role="status" className="sr-only">
          {copied ? 'Copied' : ''}
        </span>
      </p>
    </section>
  );
}
